import Screen from "@/components/layout/Screen";
import FlexParkCard, { FlexParkCardSkeleton } from "@/components/shared/FlexParkCard";
import FlexParkModal from "@/components/shared/FlexParkModal";
import PageTitle from "@/components/shared/PageTitle/PageTitle";
import PassphraseModal from "@/components/shared/PassphraseModal";
import ShowAddressModal from "@/components/shared/ShowAddressModal";
import { SkeletonText } from "@/components/ui/skeleton";
import { Text } from "@/components/ui/text";
import { VStack } from "@/components/ui/vstack";
import { useAccount } from "@/hooks/useAccount";
import { useCustomTheme } from "@/hooks/useCustomTheme";
import { usePurchaseFlexPark } from "@/hooks/usePurchaseFlexPark";
import { saveToStorage } from "@/utils/secureStore";
import { T, useT } from "@transifex/react";
import { useRouter } from "expo-router";
import { useEffect, useState } from "react";
import { Pressable } from "react-native";

export default function PurchaseFlexPark() {
  const [isAddressModalVisible, setIsAddressModalVisible] = useState<boolean>(false);
  const { account, isFetching: isAccountFetching } = useAccount();
  const {
    user,
    passes,
    isFetching,
    isModalOpen,
    selectedRateProductID,
    handlePurchase,
    setIsModalOpen,
    processPurchase,
    isPasswordModalOpen,
    setIsPasswordModalOpen,
  } = usePurchaseFlexPark();
  const { skin } = useCustomTheme();
  const t = useT();
  const router = useRouter();

  useEffect(() => {
    if (account?.phone) saveToStorage("contact", account?.phone);
  }, [account]);

  const isLoading = isFetching || isAccountFetching;

  return (
    <Screen>
      {isFetching || isAccountFetching ? <SkeletonText className="h-[24px] w-[220px] mx-auto" /> : <PageTitle title={t("Select a FlexPark to Purchase")} />}

      {isLoading ? (
        <VStack className="gap-[8px]">
          <SkeletonText className="h-[20px] w-[250px] mx-auto" />
          <SkeletonText className="h-[20px] w-[180px] mx-auto" />
        </VStack>
      ) : (
        <Text className="font-[400] leading-[20px] text-[14px] text-center text-typography-900">
          <T _str="These FlexPark passes can only be redeemed at" />{" "}
          {skin.locationName ? (
            <Text className="font-[600] leading-[20px] text-[14px] text-center text-typography-900">
              {`${skin.locationName}.`}{" "}
              {skin.locationName && skin.locationAddress && (
                <Pressable className="inline" onPress={() => setIsAddressModalVisible(true)}>
                  <Text className="font-[400] leading-[20px] text-[14px] text-center text-typography-900 underline">
                    <T _str="Learn More." />
                  </Text>
                </Pressable>
              )}
            </Text>
          ) : (
            <Text className="font-[400] leading-[20px] text-[14px] text-center text-typography-900">{t("this location")}.</Text>
          )}
        </Text>
      )}

      {isLoading ? (
        <>
          <FlexParkCardSkeleton showEventRestrictionWarning={false} />
          <FlexParkCardSkeleton showEventRestrictionWarning={false} />
          <FlexParkCardSkeleton showEventRestrictionWarning={false} />
        </>
      ) : (
        passes?.map((pass) => <FlexParkCard key={pass.rateProductID} pass={pass} onPurchase={() => handlePurchase(pass.rateProductID)} />)
      )}

      <FlexParkModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        handleConfirm={() => selectedRateProductID && processPurchase(selectedRateProductID)}
        modalType={user ? "notice" : "needtologin"}
      />

      <ShowAddressModal
        isOpen={isAddressModalVisible}
        onClose={() => setIsAddressModalVisible(false)}
        locationName={skin.locationName}
        locationAddress={skin.locationAddress?.address}
      />

      {selectedRateProductID && (
        <PassphraseModal
          isOpen={isPasswordModalOpen}
          onClose={() => setIsPasswordModalOpen(false)}
          rateID={selectedRateProductID}
          rateName={passes?.find((p) => p.rateProductID === selectedRateProductID)?.passName || ""}
          onSuccess={() => {
            processPurchase(selectedRateProductID);
          }}
        />
      )}
    </Screen>
  );
}
